import { FunctionComponent, useCallback, useEffect, useState } from "react";
import PageLayout from "@components/core/layouts/PageLayout";
import { useTranslation } from "react-i18next";
import { ROUTES } from "@routes/Routes";
import CardSection from "@components/core/section/CardSection";
import {
  GroupMembership,
  PageGroupMembership,
} from "@type/core/dashboard/dashboard.types";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import { useUnit } from "effector-react";
import { AuthenticationContext } from "@type/core/auth/auth.types";
import { authenticationStore } from "@state/auth/AuthStore";
import {
  requestDeleteGroupMembership,
  requestGetGroupAdmin,
  requestGetGroupSirens,
  requestPostDisableGroupMembership,
  requestPostGroupMembership,
  requestSearchGroupMembership,
} from "@state/group/GroupEffects";
import { toastError } from "@utils/helpers/toast-helper";
import AddGroupModal from "@components/core/modals/AddGroupModal";
import GroupMemberShipTable from "@components/core/tables/GroupMemberShipTable";

const GroupScreen: FunctionComponent = () => {
  const { t } = useTranslation();

  const [groupMembershipLoading, setGroupMembershipLoading] =
    useState<boolean>(true);

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const groupsToDisplay = authStore?.user?.groups || [];

  const [isAddGroupMembershipOpen, setIsAddGroupMembership] =
    useState<boolean>(false);
  const [isDeleteGroupMembershipOpen, setIsDeleteGroupMembershipOpen] =
    useState<boolean>(false);
  const [isDisableGroupMembershipOpen, setIsDisableGroupMembershipOpen] =
    useState<boolean>(false);
  const [selectedGroupMembership, setSelectedGroupMembership] =
    useState<GroupMembership>();

  const [groupMemberships, setGroupMemberships] =
    useState<PageGroupMembership>();
  const [groupId, setGroupId] = useState<string>(groupsToDisplay[0].id || "");
  const [adminId, setAdminId] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [sirens, setSirens] = useState<string[]>([]);
  const [sorter, setSorter] = useState<{ type: string; order: number }>({
    type: "membership",
    order: 0,
  });

  useEffect(() => {
    if (groupId) {
      void requestGetGroupSirens({ id: groupId }).then((result) => {
        if (result.ok && result.data) {
          setSirens(result.data.sirens);
        } else {
          toastError(t("offices.group.getGroupSirensError"));
        }
      });
      void requestGetGroupAdmin({ id: groupId }).then((result) => {
        if (result.data) {
          setAdminId(result.data.adminId);
        } else {
          toastError(t("offices.group.getGroupAdminError"));
        }
      });
    }
  }, [groupId, page, sorter, t]);

  const fetchMembership = useCallback(
    (newGroupId?: string) => {
      if (!newGroupId) {
        return;
      }
      if (newGroupId !== groupId) {
        setPage(1);
      }
      setGroupMembershipLoading(true);
      setGroupId(newGroupId);
      requestSearchGroupMembership({
        dto: {
          groupId: newGroupId,
          size: 10,
          search: "",
          page: page,
          sort: {
            order: sorter.order,
            type: sorter.type,
          },
        },
      })
        .catch(() => {
          toastError(t("offices.group.getMembershipError"));
        })
        .finally(() => {
          setGroupMembershipLoading(false);
        });
    },
    [groupId, page, sorter.order, sorter.type, t],
  );

  useEffect(() => {
    fetchMembership(groupId);
  }, [fetchMembership, groupId, sorter]);

  useEffect(() => {
    return requestSearchGroupMembership.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setGroupMemberships(result.data);
      } else {
        toastError(t("offices.group.getMembershipError"));
      }
    });
  });

  const addSiret = (values: { siren: string; nic: string }) => {
    void requestPostGroupMembership({
      dto: {
        adminId: adminId,
        groupId: groupId,
        membership: "",
        nic: values.nic,
        siren: values.siren,
      },
    }).then((result) => {
      if (result.ok) {
        fetchMembership(groupId);
      } else {
        toastError(t("offices.group.addGroupMembership.error"));
      }
    });
  };

  const deleteSiret = () => {
    void requestDeleteGroupMembership({
      id: selectedGroupMembership?.id,
    }).then((result) => {
      if (result.ok) {
        fetchMembership(groupId);
      } else {
        toastError(t("offices.group.deleteGroupMembership.error"));
      }
    });
  };

  const disableSiret = () => {
    void requestPostDisableGroupMembership({
      id: selectedGroupMembership?.id,
    }).then((result) => {
      if (result.ok) {
        fetchMembership(groupId);
      } else {
        toastError(t("offices.group.disableGroupMembership.error"));
      }
    });
  };

  return (
    <PageLayout container returnRoute={ROUTES.extranet.home.generate()}>
      <>
        <CardSection title={t("offices.group.title")}>
          <GroupMemberShipTable
            isLoading={groupMembershipLoading}
            groupsToDisplay={groupsToDisplay}
            handleSearch={fetchMembership}
            setIsAddGroupMembership={setIsAddGroupMembership}
            setIsDeleteGroupMembershipOpen={setIsDeleteGroupMembershipOpen}
            setIsDisableGroupMembershipOpen={setIsDisableGroupMembershipOpen}
            setSelectedGroupMembership={setSelectedGroupMembership}
            groupMemberships={groupMemberships}
            setSorter={setSorter}
            setPage={setPage}
          ></GroupMemberShipTable>
        </CardSection>
        <AddGroupModal
          visible={isAddGroupMembershipOpen}
          onCancel={() => setIsAddGroupMembership(false)}
          onOk={(values) => {
            setIsAddGroupMembership(false);
            addSiret(values);
          }}
          sirens={sirens}
          title={t("offices.group.addGroupMembership.title")}
        />
        <ConfirmModal
          visible={isDeleteGroupMembershipOpen}
          onCancel={() => setIsDeleteGroupMembershipOpen(false)}
          onOk={() => {
            setIsDeleteGroupMembershipOpen(false);
            deleteSiret();
          }}
          title={t("offices.group.deleteGroupMembership.title", {
            membership: selectedGroupMembership?.membership,
          })}
        />
        <ConfirmModal
          visible={isDisableGroupMembershipOpen}
          onCancel={() => setIsDisableGroupMembershipOpen(false)}
          onOk={() => {
            setIsDisableGroupMembershipOpen(false);
            disableSiret();
          }}
          title={t("offices.group.disableGroupMembership.title", {
            membership: selectedGroupMembership?.membership,
          })}
        />
      </>
    </PageLayout>
  );
};

export default GroupScreen;
